import { useEffect, useMemo, useState } from 'react';

import env from '@config/env';
import { useFuturesBalance } from '@hooks/queries/useFuturesBalance';
import { usePositions } from '@hooks/queries/usePositions';
import { useSpotAssets } from '@hooks/queries/useSpotAssets';
import { IFuturesBalance, IWalletsBalance } from '@interfaces/balances';
import { IPosition } from '@interfaces/position';
import { IWalletAsset } from '@interfaces/wallet-asset';

import { POSITION_SIDE } from '../constants';
import { roundAmount } from '../utils/amount';

import { useThrottledValue } from './useThrottledValue';

type MarketType = 'spot' | 'futures';

type PriceMap = Record<string, number>;

type TickerUpdate = {
  symbol: string;
  price: number;
};

const STABLE_COINS = new Set(['USDT', 'USDC', 'BUSD', 'DAI', 'TUSD', 'USDP']);

const DEFAULT_WS_URL: Record<MarketType, string> = {
  spot: 'wss://stream.binance.com:9443/ws',
  futures: 'wss://fstream.binance.com/ws',
};

const DEFAULT_REST_URL: Record<MarketType, string> = {
  spot: 'https://api.binance.com/api/v3/ticker/price',
  futures: 'https://fapi.binance.com/fapi/v1/ticker/price',
};

const getMarketConfig = (market: MarketType) => ({
  wsUrl:
    market === 'spot'
      ? env.MARKET_SPOT_WS_URL ?? DEFAULT_WS_URL.spot
      : env.MARKET_FUTURES_WS_URL ?? DEFAULT_WS_URL.futures,
  restUrl:
    market === 'spot'
      ? env.MARKET_SPOT_REST_URL ?? DEFAULT_REST_URL.spot
      : env.MARKET_FUTURES_REST_URL ?? DEFAULT_REST_URL.futures,
});

const safeParse = (value?: string) => {
  const parsed = parseFloat(value ?? '0');

  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value: number, decimals = 8) => {
  if (!Number.isFinite(value)) {
    return '0';
  }

  const precision = Math.abs(value) >= 1000 ? 2 : decimals;
  const fixed = value.toFixed(precision);
  const trimmed = fixed.replace(/\.0+$/, '').replace(/(\.[0-9]*?)0+$/, '$1');
  const normalized = trimmed === '-0' ? '0' : trimmed;

  return normalized || '0';
};

const extractTickerUpdate = (payload: any): TickerUpdate | null => {
  if (!payload) {
    return null;
  }

  const data = payload.data ?? payload;
  const symbol = data?.s ?? data?.symbol;
  const rawPrice = data?.c ?? data?.price ?? data?.p ?? data?.markPrice ?? data?.lastPrice;

  if (!symbol || rawPrice === undefined) {
    return null;
  }

  const price = parseFloat(rawPrice);

  if (Number.isNaN(price)) {
    return null;
  }

  return {
    symbol: String(symbol).toUpperCase(),
    price,
  };
};

const useMarketTicker = (symbols: string[], market: MarketType) => {
  const [prices, setPrices] = useState<PriceMap>({});
  const sortedSymbols = useMemo(() => Array.from(new Set(symbols)).sort(), [symbols]);
  const joinedSymbols = sortedSymbols.join(',');

  useEffect(() => {
    if (!sortedSymbols.length) {
      setPrices({});
      return;
    }

    if (typeof WebSocket === 'undefined') {
      return;
    }

    setPrices((prev) => {
      const next: PriceMap = {};

      sortedSymbols.forEach((symbol) => {
        if (prev[symbol] !== undefined) {
          next[symbol] = prev[symbol];
        }
      });

      return next;
    });

    let isActive = true;
    let ws: WebSocket | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

    const { wsUrl } = getMarketConfig(market);

    const scheduleReconnect = () => {
      if (!isActive) {
        return;
      }

      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      reconnectTimeout = setTimeout(connect, 2000);
    };

    const connect = () => {
      if (!isActive || !sortedSymbols.length) {
        return;
      }

      try {
        ws = new WebSocket(wsUrl);
      } catch {
        scheduleReconnect();
        return;
      }

      ws.onopen = () => {
        const params = sortedSymbols.map((symbol) => `${symbol.toLowerCase()}@miniTicker`);

        if (!params.length) {
          ws?.close();
          return;
        }

        ws?.send(
          JSON.stringify({
            method: 'SUBSCRIBE',
            params,
            id: Date.now(),
          }),
        );
      };

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          const update = extractTickerUpdate(payload);

          if (update) {
            setPrices((prev) => {
              if (prev[update.symbol] === update.price) {
                return prev;
              }

              return {
                ...prev,
                [update.symbol]: update.price,
              };
            });
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        if (!isActive) {
          return;
        }

        scheduleReconnect();
      };

      ws.onerror = () => {
        // rely on close handler for reconnect
      };
    };

    connect();

    return () => {
      isActive = false;

      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      if (ws) {
        try {
          ws.close();
        } catch {
          // ignore errors during closing
        }
      }
    };
  }, [market, joinedSymbols]);

  useEffect(() => {
    if (!sortedSymbols.length) {
      return;
    }

    if (typeof fetch === 'undefined') {
      return;
    }

    let isActive = true;
    const controller = new AbortController();
    const { restUrl } = getMarketConfig(market);

    const fetchInitialPrices = async () => {
      try {
        const responses = await Promise.all(
          sortedSymbols.map(async (symbol) => {
            try {
              const response = await fetch(`${restUrl}?symbol=${symbol}`, {
                signal: controller.signal,
              });

              if (!response.ok) {
                return null;
              }

              const data = await response.json();
              const update = extractTickerUpdate(data);

              if (update) {
                return update;
              }

              const alternativePrice = data?.price ?? data?.c ?? data?.markPrice ?? data?.lastPrice;
              const numericPrice = parseFloat(alternativePrice);

              if (Number.isNaN(numericPrice)) {
                return null;
              }

              return {
                symbol,
                price: numericPrice,
              };
            } catch (error: any) {
              if (error?.name === 'AbortError') {
                return null;
              }

              return null;
            }
          }),
        );

        if (!isActive) {
          return;
        }

        setPrices((prev) => {
          const next: PriceMap = { ...prev };

          responses.forEach((update) => {
            if (update) {
              next[update.symbol] = update.price;
            }
          });

          return next;
        });
      } catch {
        // ignore fetch errors
      }
    };

    fetchInitialPrices();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [market, joinedSymbols]);

  return prices;
};

const useSpotAssetsRealtime = (assets?: IWalletAsset[]) => {
  const trackedSymbols = useMemo(() => {
    if (!assets?.length) {
      return [] as string[];
    }

    const symbols = new Set<string>();

    assets.forEach((asset) => {
      const coin = asset.coin.toUpperCase();
      const amount = safeParse(asset.amount);

      if (!amount || STABLE_COINS.has(coin)) {
        return;
      }

      symbols.add(`${coin}USDT`);
    });

    return Array.from(symbols);
  }, [assets]);

  const prices = useMarketTicker(trackedSymbols, 'spot');

  const assetsWithBalance = useMemo(() => {
    if (!assets?.length) {
      return [] as IWalletAsset[];
    }

    return assets.map((asset) => {
      const coin = asset.coin.toUpperCase();
      const amount = safeParse(asset.amount);

      if (!amount) {
        return {
          ...asset,
          price: asset.price ?? '0',
          amountInUSDT: '0',
        };
      }

      if (STABLE_COINS.has(coin)) {
        return {
          ...asset,
          price: '1',
          amountInUSDT: formatNumber(amount, 6),
        };
      }

      const ticker = `${coin}USDT`;
      const price = prices[ticker];

      if (price === undefined) {
        return {
          ...asset,
          price: asset.price,
          amountInUSDT: asset.amountInUSDT,
        };
      }

      const value = amount * price;

      return {
        ...asset,
        price: formatNumber(price, 6),
        amountInUSDT: formatNumber(value, 6),
      };
    });
  }, [assets, prices]);

  const spotBalance = useMemo(() => {
    if (!assetsWithBalance.length) {
      return '0';
    }

    const total = assetsWithBalance.reduce((acc, asset) => acc + safeParse(asset.amountInUSDT), 0);

    return formatNumber(total, 6);
  }, [assetsWithBalance]);

  return {
    assets: assetsWithBalance,
    balance: spotBalance,
  };
};

const useFuturesRealtime = (positions?: IPosition[], balance?: IFuturesBalance) => {
  const symbols = useMemo(() => {
    if (!positions?.length) {
      return [] as string[];
    }

    return Array.from(new Set(positions.map((position) => position.symbol))).sort();
  }, [positions]);

  const prices = useMarketTicker(symbols, 'futures');

  const positionsWithRealtime = useMemo(() => {
    if (!positions?.length) {
      return [] as IPosition[];
    }

    return positions.map((position) => {
      const markPrice = prices[position.symbol];

      if (markPrice === undefined) {
        return position;
      }

      const entryPrice = safeParse(position.entryPrice);
      const quantity = safeParse(position.quantity);

      if (!entryPrice || !quantity) {
        return {
          ...position,
          markPrice: formatNumber(markPrice, 6),
        };
      }

      const priceDifference =
        position.side === POSITION_SIDE.SHORT ? entryPrice - markPrice : markPrice - entryPrice;

      const pnlValue = priceDifference * quantity;
      const pnlBase = entryPrice * quantity;
      const pnlPercentage = pnlBase === 0 ? 0 : (pnlValue / pnlBase) * 100;

      return {
        ...position,
        markPrice: formatNumber(markPrice, 6),
        pnl: formatNumber(pnlValue, 6),
        pnlPercentage: formatNumber(pnlPercentage, 4),
      };
    });
  }, [positions, prices]);

  const totalRealtimePnl = useMemo(() => {
    if (!positionsWithRealtime.length) {
      return 0;
    }

    return positionsWithRealtime.reduce((acc, position) => acc + safeParse(position.pnl), 0);
  }, [positionsWithRealtime]);

  const futuresBalanceRealtime = useMemo(() => {
    if (!balance) {
      return balance;
    }

    const basePnl = safeParse(balance.totalUnrealizedProfit);
    const diff = totalRealtimePnl - basePnl;

    return {
      availableBalance: formatNumber(safeParse(balance.availableBalance) + diff, 6),
      totalUnrealizedProfit: formatNumber(totalRealtimePnl, 6),
      totalMarginBalance: formatNumber(safeParse(balance.totalMarginBalance) + diff, 6),
    };
  }, [balance, totalRealtimePnl]);

  return {
    positions: positionsWithRealtime,
    balance: futuresBalanceRealtime,
  };
};

type UseWalletRealtimeOptions = {
  throttleMs?: number;
};

const DEFAULT_THROTTLE_MS = 500;

export const useWalletRealtime = (options?: UseWalletRealtimeOptions) => {
  const { data: spotAssets, isLoading: spotAssetsLoading } = useSpotAssets(true);
  const { data: futuresPositions, isLoading: positionsLoading } = usePositions(true);
  const { data: futuresBalance, isLoading: futuresBalanceLoading } = useFuturesBalance();

  const { assets, balance: spotBalance } = useSpotAssetsRealtime(spotAssets);
  const { positions, balance: futuresBalanceRealtime } = useFuturesRealtime(
    futuresPositions,
    futuresBalance,
  );

  const throttleMs = options?.throttleMs ?? DEFAULT_THROTTLE_MS;
  const throttledAssets = useThrottledValue(assets, throttleMs);
  const throttledPositions = useThrottledValue(positions, throttleMs);
  const throttledSpotBalance = useThrottledValue(spotBalance, throttleMs);
  const futuresBalanceCombined = futuresBalanceRealtime ?? futuresBalance;
  const throttledFuturesBalance = useThrottledValue(futuresBalanceCombined, throttleMs);

  const walletBalance = useMemo<IWalletsBalance>(() => {
    const spot = spotBalance;
    const futuresUSDM =
      futuresBalanceRealtime?.totalMarginBalance ?? futuresBalance?.totalMarginBalance ?? '0';

    const total = roundAmount(safeParse(spot) + safeParse(futuresUSDM), 6);

    return {
      spot,
      futuresUSDM,
      total,
    };
  }, [spotBalance, futuresBalanceRealtime, futuresBalance]);

  const throttledWalletBalance = useThrottledValue(walletBalance, throttleMs);

  return {
    spotAssets: throttledAssets,
    futuresPositions: throttledPositions,
    spotBalance: throttledSpotBalance,
    futuresBalance: throttledFuturesBalance,
    walletBalance: throttledWalletBalance,
    spotAssetsLoading,
    positionsLoading,
    futuresBalanceLoading,
  };
};
